/**
 * Diagnostics / log upload section (GetDiagnostics 1.6 + GetLog 2.0.1).
 *
 * A browser-only sim can't upload to the CSMS-supplied target (FTP is
 * unreachable; HTTP usually fails CORS), so the reported status is the real
 * result and this panel lets the tester Save the generated bundle to disk and
 * — after placing it where the CSMS expects — Mark Uploaded to drive the CSMS
 * happy-path.
 */
import { useState } from 'react';
import { ConnectionState } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { useDiagnosticsStore, type DiagnosticsStatus } from '@/state/useDiagnosticsStore';
import { formatDateTime } from '@/lib/format';

const STATUS_TONE: Record<DiagnosticsStatus, string> = {
  idle: 'bg-slate-700/80 text-slate-300',
  uploading: 'bg-sky-700/80 text-sky-50',
  uploaded: 'bg-primary-base/80 text-primary-on',
  failed: 'bg-rose-700/80 text-rose-50',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function DiagnosticsSection(): JSX.Element {
  const status = useDiagnosticsStore((s) => s.status);
  const kind = useDiagnosticsStore((s) => s.kind);
  const location = useDiagnosticsStore((s) => s.location);
  const filename = useDiagnosticsStore((s) => s.filename);
  const sizeBytes = useDiagnosticsStore((s) => s.sizeBytes);
  const sha256 = useDiagnosticsStore((s) => s.sha256);
  const error = useDiagnosticsStore((s) => s.error);
  const at = useDiagnosticsStore((s) => s.at);
  const saveToDisk = useDiagnosticsStore((s) => s.saveToDisk);
  const clear = useDiagnosticsStore((s) => s.clear);
  const markUploaded = useStationStore((s) => s.markDiagnosticsUploaded);
  const isConnected = useStationStore((s) => s.connectionState) === ConnectionState.Connected;

  const [open, setOpen] = useState(false);
  const hasBundle = sizeBytes !== null;
  const canMark = hasBundle && isConnected && status !== 'uploaded';

  return (
    <section
      className="rounded border border-slate-700 bg-slate-900/60 p-3"
      data-testid="diagnostics-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="diagnostics-body"
        className="flex w-full items-center justify-between gap-2 text-left focus:outline-none focus:ring-2 focus:ring-primary-base"
        data-testid="diagnostics-toggle"
      >
        <span className="flex items-center gap-1.5">
          <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            className={`h-3 w-3 fill-current text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
          >
            <path d="M5.5 3.5L10.5 8l-5 4.5z" />
          </svg>
          <span className="text-sm font-medium text-slate-100">
            Diagnostics upload (GetDiagnostics / GetLog)
          </span>
        </span>
        <span className="flex items-center gap-2">
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_TONE[status]}`}
            data-testid="diagnostics-status"
          >
            {status}
          </span>
          <span className="text-[10px] text-slate-500">{open ? 'hide' : 'expand'}</span>
        </span>
      </button>

      {open ? (
        <div id="diagnostics-body" className="mt-3 space-y-2 text-xs">
          <p className="text-[11px] text-slate-500">
            A browser can&apos;t upload to an ftp:// target (or a CORS-blocked URL), so the status
            reflects the real result. Save the generated bundle to disk, put it where the CSMS
            expects, then Mark Uploaded to complete the flow.
          </p>

          {status === 'idle' ? (
            <p className="text-[11px] text-slate-500" data-testid="diagnostics-empty">
              No diagnostics requested yet. Trigger GetDiagnostics / GetLog from the CSMS.
            </p>
          ) : (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-slate-300">
              <dt className="text-slate-500">Kind</dt>
              <dd>{kind ?? '—'}</dd>
              <dt className="text-slate-500">Target</dt>
              <dd className="break-all font-mono text-[11px]" data-testid="diagnostics-target">
                {location ?? '—'}
              </dd>
              <dt className="text-slate-500">File</dt>
              <dd className="font-mono text-[11px]">{filename ?? '—'}</dd>
              <dt className="text-slate-500">Size</dt>
              <dd>{sizeBytes !== null ? formatBytes(sizeBytes) : '—'}</dd>
              <dt className="text-slate-500">SHA-256</dt>
              <dd className="break-all font-mono text-[11px]" data-testid="diagnostics-sha256">
                {sha256 ?? '—'}
              </dd>
              {at !== null ? (
                <>
                  <dt className="text-slate-500">At</dt>
                  <dd className="text-[11px]">{formatDateTime(at)}</dd>
                </>
              ) : null}
            </dl>
          )}

          {error !== null ? (
            <p
              className="rounded bg-rose-950/60 px-2 py-1 text-[11px] text-rose-300"
              data-testid="diagnostics-error"
            >
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={saveToDisk}
              disabled={!hasBundle}
              title={hasBundle ? 'Save the diagnostics bundle to disk' : 'No bundle generated'}
              className="rounded bg-primary-hover px-3 py-1 text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="diagnostics-save"
            >
              Save to disk
            </button>
            <button
              type="button"
              onClick={() => {
                void markUploaded();
              }}
              disabled={!canMark}
              title={
                canMark
                  ? 'Report this upload to the CSMS as Uploaded'
                  : isConnected
                    ? 'No bundle to report (or already Uploaded)'
                    : 'Connect first'
              }
              className="rounded bg-emerald-700 px-3 py-1 text-emerald-50 hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="diagnostics-mark-uploaded"
            >
              Mark Uploaded
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={status === 'idle'}
              className="rounded bg-slate-700 px-3 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="diagnostics-clear"
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
